import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { DevotionalData } from '../lib/devotionalTemplate';

export interface FamilyDevotionalRecord {
  id: string;
  sermon_id: string | null;
  sermon_title: string;
  scripture: string | null;
  date: string | null;
  devotional_data: DevotionalData;
  created_at: string;
}

export function useFamilyDevotionals() {
  const [devotionals, setDevotionals] = useState<FamilyDevotionalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: fetchError } = await supabase
        .from('family_devotionals')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setDevotionals(data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load devotionals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (
    sermonId: string,
    sermonTitle: string,
    scripture: string,
    date: string | undefined,
    devotionalData: DevotionalData
  ): Promise<FamilyDevotionalRecord> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error: insertError } = await supabase
      .from('family_devotionals')
      .insert([{
        user_id: user.id,
        sermon_id: sermonId,
        sermon_title: sermonTitle,
        scripture: scripture || null,
        date: date || null,
        devotional_data: devotionalData,
      }])
      .select()
      .single();

    if (insertError) throw insertError;
    await load();
    return data;
  };

  const update = async (id: string, devotionalData: DevotionalData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error: updateError } = await supabase
      .from('family_devotionals')
      .update({ devotional_data: devotionalData })
      .eq('id', id)
      .eq('user_id', user.id);

    if (updateError) throw updateError;
    setDevotionals(prev =>
      prev.map(d => d.id === id ? { ...d, devotional_data: devotionalData } : d)
    );
  };

  const remove = async (id: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error: deleteError } = await supabase
      .from('family_devotionals')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);

    if (deleteError) throw deleteError;
    setDevotionals(prev => prev.filter(d => d.id !== id));
  };

  return { devotionals, loading, error, save, update, remove };
}
